import { defineComponent as _defineComponent } from 'vue'
import { reactive, computed, ref, watch } from 'vue';
import OptionDetailsFormGroup from './forms/OptionDetailsFormGroup.vue';
import OptionReview from './OptionReview.vue';
import OptionConfirmation from './OptionConfirmation.vue';
import { AmountType, VanillaPriceResponse } from 'ah-api-gateways/models';
import CountdownTimer from 'ah-common-lib/src/common/components/CountdownTimer.vue';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useTradeState } from '..';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';
import { cloneDeep } from 'lodash';

enum OptionsCreatorSteps {
  OPTIONS_CREATOR = 'optionCreator',
  OPTIONS_REVIEW = 'optionsReview',
  OPTIONS_BOOKED = 'optionsBooked',
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OptionCreator',
  setup(__props) {

const optionDetailsFormGroup = ref<InstanceType<typeof OptionDetailsFormGroup> | null>(null);

const state = reactive<{
  selectedSolution?: VanillaPriceResponse;
  solutions: VanillaPriceResponse[];
  stage: OptionsCreatorSteps;
  createdTrade?: string;
  loading: boolean;
  pause: boolean;
  defaultDate: Date | null;
  validations: any[];
}>({
  solutions: [],
  stage: OptionsCreatorSteps.OPTIONS_CREATOR,
  loading: false,
  pause: false,
  defaultDate: null,
  validations: [],
});

function setSolution(selectedSolution: VanillaPriceResponse) {
  state.selectedSolution = selectedSolution;
}

const title = computed(() => (state.stage === OptionsCreatorSteps.OPTIONS_CREATOR ? 'Trade details' : 'Review'));
const requestManager = useRequestManager();
const tradeState = useTradeState();
const onBehalfOfClient = useOnBehalfOf();

const clientId = computed(
  () => tradeState.store.useAuthStore().loggedInIdentity?.client?.id ?? onBehalfOfClient.value?.id
);

function isInvalidSolution(solutionId: string) {
  const index = state.validations.findIndex((validation) => validation.solutionId === solutionId);

  return index >= 0 ? state.validations[index].validation : false;
}

function refreshRates() {
  if (state.stage === OptionsCreatorSteps.OPTIONS_CREATOR) {
    state.defaultDate = new Date(new Date().setMinutes(new Date().getMinutes() + 1));
    state.solutions.forEach((solution) => {
      if (!isInvalidSolution(solution.id)) {
        optionDetailsFormGroup.value?.reloadPrices(createVanillaOption(solution), solution.id);
      }
    });
  } else if (state.stage === OptionsCreatorSteps.OPTIONS_REVIEW && state.selectedSolution) {
    requestManager.manager
      .sameOrCancelAndNew(
        'createVanillaOptions' + state.selectedSolution.id,
        tradeState.services.pricingEngine.createVanillaOptionsQuote(
          createVanillaOption(state.selectedSolution),
          onBehalfOfClient.value?.id
        )
      )
      .subscribe((price) => {
        const index = state.solutions.findIndex((sol) => sol.id === state.selectedSolution!.id);
        const solutions = cloneDeep(state.solutions);

        let premiumCurrency;
        if (solutions[index].premiumCurrency) {
          premiumCurrency = solutions[index].premiumCurrency;
        } else {
          premiumCurrency = price.ccy1.amountType === AmountType.SELL ? price.ccy1.currency : price.ccy2.currency;
        }

        solutions[index] = { ...price, premiumCurrency };

        state.selectedSolution = solutions[index];

        state.solutions = solutions;
      });
  }
}

function createVanillaOption(solution: VanillaPriceResponse) {
  const isPrimaryRate = solution.ccy1.isPrimaryRate === true ? solution.ccy1 : solution.ccy2;
  const isFixedSide = solution.ccy1.isFixedSide === true ? solution.ccy1 : solution.ccy2;
  const sellCurrency = solution.ccy1.amountType === AmountType.SELL ? solution.ccy1.currency : solution.ccy2.currency;
  const buyCurrency = solution.ccy1.amountType === AmountType.BUY ? solution.ccy1.currency : solution.ccy2.currency;

  return {
    sellCurrency: sellCurrency,
    buyCurrency: buyCurrency,
    amount: isFixedSide.clientAmount,
    amountType: isFixedSide.amountType,
    settlementDate: formatDate(new Date(solution.settlementDate), 'yyyy-MM-dd'),
    strikeRate: isPrimaryRate.optionsCurrencyData.selectedStrikeRate,
    clientId: clientId.value,
  };
}

const earliestPriceExpiry = computed(() => {
  if (state.solutions.length === 0) {
    return undefined;
  }

  if (state.stage === OptionsCreatorSteps.OPTIONS_REVIEW && state.selectedSolution) {
    return new Date(state.selectedSolution.priceExpirationTimestamp);
  }

  return state.defaultDate;
});

watch(
  () => state.solutions,
  (newVal, oldVal) => {
    if (oldVal.length === 0 && newVal.length === 1) {
      state.defaultDate = new Date(new Date().setMinutes(new Date().getMinutes() + 1));
    }
  }
);

return { __sfc: true,optionDetailsFormGroup, OptionsCreatorSteps, state, setSolution, title, requestManager, tradeState, onBehalfOfClient, clientId, isInvalidSolution, refreshRates, createVanillaOption, earliestPriceExpiry, OptionDetailsFormGroup, OptionReview, OptionConfirmation, CountdownTimer }
}

})